import type {
  ActivityEvent,
  ActivityLane,
  ActivityStatus,
  ActivitiesFilters,
  DayActivitySummary,
} from "./activitiesTypes";

const DONE_STATUSES = new Set<ActivityStatus>(["completed", "failed", "denied", "approved"]);

export function activityLane(event: ActivityEvent): ActivityLane {
  if (event.status === "running") return "doing";
  if (
    event.status === "needs_approval" ||
    event.status === "pending" ||
    (event.kind === "schedule" && event.dueAt && !DONE_STATUSES.has(event.status))
  ) {
    return "todo";
  }
  if (DONE_STATUSES.has(event.status)) return "done";
  return "todo";
}

export function isSameLocalDay(iso: string, dayKey: string): boolean {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}` === dayKey;
}

/** Events whose occurred_at or due_at falls on the selected calendar day. */
export function eventsForDay(events: readonly ActivityEvent[], dayKey: string): ActivityEvent[] {
  return events.filter(
    (e) =>
      isSameLocalDay(e.occurredAt, dayKey) || (e.dueAt ? isSameLocalDay(e.dueAt, dayKey) : false),
  );
}

export function filterActivities(
  events: readonly ActivityEvent[],
  filters: ActivitiesFilters,
): ActivityEvent[] {
  return events.filter((e) => {
    if (filters.memberUserId && e.actor.userId !== filters.memberUserId) return false;
    if (filters.status !== "all" && e.status !== filters.status) return false;
    if (filters.kind !== "all" && e.kind !== filters.kind) return false;
    return true;
  });
}

export function groupByLane(
  events: readonly ActivityEvent[],
): Record<ActivityLane, ActivityEvent[]> {
  const lanes: Record<ActivityLane, ActivityEvent[]> = { todo: [], doing: [], done: [] };
  for (const event of events) {
    lanes[activityLane(event)].push(event);
  }
  for (const lane of Object.keys(lanes) as ActivityLane[]) {
    lanes[lane].sort((a, b) => {
      const ta = new Date(a.occurredAt).getTime();
      const tb = new Date(b.occurredAt).getTime();
      return tb - ta;
    });
  }
  return lanes;
}

export function summarizeDay(events: readonly ActivityEvent[]): DayActivitySummary {
  let todo = 0;
  let doing = 0;
  let done = 0;
  for (const event of events) {
    const lane = activityLane(event);
    if (lane === "todo") todo += 1;
    else if (lane === "doing") doing += 1;
    else done += 1;
  }
  return { todo, doing, done };
}

export function localDayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function addDays(date: Date, delta: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + delta);
  return next;
}

/** Days on each side of the selected day in the horizontal strip. */
export const ACTIVITIES_DAY_STRIP_RADIUS = 45;

/** Scrollable day strip centered on the selected day. */
export function dayStripAround(selected: Date, radius = ACTIVITIES_DAY_STRIP_RADIUS): string[] {
  const keys: string[] = [];
  for (let i = -radius; i <= radius; i += 1) {
    keys.push(localDayKey(addDays(selected, i)));
  }
  return keys;
}
